// iot-admin/client/src/pages/SuperAdminMonitoring.js
// Cross-city operational dashboard for super-admins.
// Surfaces, per active city: hex coverage, sentinels, last calibration (with
// validation gap), transit ingest age, OSM/GTFS freshness, and last onboarding
// state. Drives the FAANG SLO clock — if anything here goes red, somebody owns it.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cityService } from '../services/api';
import './SuperAdminMonitoring.css';

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

const slaBadge = (label, ok, warn) => {
  const cls = ok ? 'sla-ok' : warn ? 'sla-warn' : 'sla-bad';
  return <span className={`sla-badge ${cls}`}>{label}</span>;
};

export default function SuperAdminMonitoring() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshAt, setRefreshAt] = useState(Date.now());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    cityService
      .getSuperAdminMonitoring()
      .then((r) => { if (mounted) { setData(r); setError(null); } })
      .catch((e) => { if (mounted) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [refreshAt]);

  const cities = data?.cities || [];

  // Aggregate KPIs across all active cities.
  const kpis = cities.reduce(
    (acc, c) => {
      acc.cities += 1;
      if (c.status === 'active') acc.active += 1;
      acc.hex += Number(c.hex_count_res9 || 0);
      acc.sentinels += Number(c.sentinel_count || 0);
      acc.transit += Number(c.transit_stop_count || 0);
      if (c.last_mean_error_pp != null && Number(c.last_mean_error_pp) > 10) acc.calibAlerts += 1;
      return acc;
    },
    { cities: 0, active: 0, hex: 0, sentinels: 0, transit: 0, calibAlerts: 0 }
  );

  return (
    <div className="superadmin-page">
      <header className="superadmin-header">
        <h1>Super-Admin · Cross-City Operations</h1>
        <div className="superadmin-actions">
          <span className="refresh-stamp">
            {data?.generated_at ? `as of ${new Date(data.generated_at).toLocaleTimeString()}` : ''}
          </span>
          <button className="btn-refresh" onClick={() => setRefreshAt(Date.now())} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error">Error: {error}</div>}

      <div className="kpi-grid">
        <div className="kpi"><div className="kpi-label">Cities (active / total)</div><div className="kpi-value">{kpis.active} / {kpis.cities}</div></div>
        <div className="kpi"><div className="kpi-label">Res-9 hexes</div><div className="kpi-value">{fmtNum(kpis.hex)}</div></div>
        <div className="kpi"><div className="kpi-label">Active sentinels</div><div className="kpi-value">{fmtNum(kpis.sentinels)}</div></div>
        <div className="kpi"><div className="kpi-label">Transit stops</div><div className="kpi-value">{fmtNum(kpis.transit)}</div></div>
        <div className={`kpi ${kpis.calibAlerts ? 'kpi-warn' : ''}`}>
          <div className="kpi-label">Calibration alerts</div>
          <div className="kpi-value">{kpis.calibAlerts}</div>
        </div>
      </div>

      <div className="superadmin-table-wrap">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th>City</th>
              <th>Status</th>
              <th>Hex (res 8/9/10)</th>
              <th>Land/Ocean</th>
              <th>Sentinels (active/cand)</th>
              <th>Last calibration (mean / gap)</th>
              <th>Ola labels</th>
              <th>NRSC labels</th>
              <th>Transit (OSM/GTFS)</th>
              <th>OSM age</th>
              <th>GTFS age</th>
              <th>Last onboarding</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cities.map((c) => {
              const meanErr = c.last_mean_error_pp == null ? null : Number(c.last_mean_error_pp);
              const usable = c.last_cat_usable_pct == null ? null : Number(c.last_cat_usable_pct);
              const gapPp = c.last_calibration_gap_pp == null ? null : Number(c.last_calibration_gap_pp);
              const calibOk = meanErr != null && meanErr <= 10 && usable != null && usable >= 90 && (gapPp == null || gapPp <= 2);
              const calibWarn = meanErr != null && meanErr <= 15 && (gapPp == null || gapPp <= 4);
              const land = Number(c.land_cells || 0);
              const ocean = Number(c.ocean_cells || 0);
              const unknown = Number(c.unknown_cells || 0);
              const totalRes9 = land + ocean + unknown;
              const olaPct = land > 0 ? Math.round((Number(c.ola_labeled_cells || 0) / land) * 100) : 0;
              const nrscPct = land > 0 ? Math.round((Number(c.nrsc_labeled_cells || 0) / land) * 100) : 0;
              return (
                <tr key={c.id}>
                  <td><Link to={`/cities/${c.id}`}>{c.name}</Link></td>
                  <td><span className={`status status-${c.status}`}>{c.status}</span></td>
                  <td className="num">{fmtNum(c.hex_count_res8)} / {fmtNum(c.hex_count_res9)} / {fmtNum(c.hex_count_res10)}</td>
                  <td className="num">
                    {totalRes9 > 0
                      ? <>{fmtNum(land)}/{fmtNum(ocean)}{unknown > 0 && <span className="muted"> +{fmtNum(unknown)}?</span>}</>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num">{c.sentinel_count} <span className="muted">/ {c.sentinel_candidate_count}</span></td>
                  <td>
                    {meanErr == null
                      ? <span className="muted">never run</span>
                      : <>
                          {slaBadge(`${meanErr}pp${gapPp != null ? ` · gap ${gapPp}pp` : ''} · ${usable}%`, calibOk, calibWarn)}
                          <div className="sub">{fmtAgo(c.last_calibration_at)}</div>
                        </>}
                  </td>
                  <td className="num">
                    {c.ola_labeled_cells > 0
                      ? <>{olaPct}% <span className="muted">({fmtNum(c.ola_labeled_cells)})</span><div className="sub">{fmtAgo(c.ola_last_verified)}</div></>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num">
                    {c.nrsc_labeled_cells > 0
                      ? <>{nrscPct}% <span className="muted">({fmtNum(c.nrsc_labeled_cells)})</span><div className="sub">{fmtAgo(c.nrsc_last_verified)}</div></>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num">{fmtNum(c.transit_osm_count)} <span className="muted">/ {fmtNum(c.transit_gtfs_count)}</span></td>
                  <td>{fmtAgo(c.osm_last_ingested_at)}</td>
                  <td>{c.gtfs_url ? fmtAgo(c.gtfs_last_ingested_at) : <span className="muted">no GTFS</span>}</td>
                  <td>
                    {c.last_onboarding_status
                      ? <span className={`status status-${c.last_onboarding_status}`}>{c.last_onboarding_status}</span>
                      : <span className="muted">—</span>}
                    {c.last_onboarding_at && <div className="sub">{fmtAgo(c.last_onboarding_at)}</div>}
                  </td>
                  <td><Link className="btn-small" to={`/cities/${c.id}`}>Open →</Link></td>
                </tr>
              );
            })}
            {!loading && cities.length === 0 && (
              <tr><td colSpan="13" className="empty">No active cities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="legend">
        <strong>Calibration SLO:</strong> green ≤ 10pp mean error AND ≥ 90% category-usable; yellow ≤ 15pp; red otherwise.<br/>
        <strong>OSM / GTFS age:</strong> targets are weekly OSM, monthly GTFS where published.
      </p>
    </div>
  );
}
